const E2EE_PREFIX = "e2ee:v1:";

export type E2EEStatus = {
  enabled: boolean;
  ready: boolean;
  securityCode: string | null;
  mediaLayer: "dtls-srtp" | "insertable-streams";
  chatEncrypted: boolean;
  verified: boolean;
};

function bytesToHex(bytes: ArrayBuffer): string {
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function formatSecurityCode(fingerprintHex: string): string {
  return fingerprintHex.slice(0, 32).match(/.{1,4}/g)?.join(" ") ?? fingerprintHex;
}

export class RoomE2EESession {
  private keyPair: CryptoKeyPair | null = null;
  private mediaKey: CryptoKey | null = null;
  private mediaKeyRaw: ArrayBuffer | null = null;
  private fingerprint: string | null = null;
  private peerKeys = new Map<string, CryptoKey>();
  private enabled = false;
  private insertableSupported =
    typeof RTCRtpScriptTransform !== "undefined" &&
    typeof RTCRtpSender !== "undefined";

  constructor(private peerId: string) {}

  async enable(): Promise<void> {
    this.enabled = true;
    this.keyPair = await crypto.subtle.generateKey(
      { name: "ECDH", namedCurve: "P-256" },
      true,
      ["deriveKey"],
    );
  }

  isEnabled() {
    return this.enabled;
  }

  async exportPublicKeyJwk(): Promise<JsonWebKey> {
    if (!this.keyPair) throw new Error("E2EE not initialized");
    return crypto.subtle.exportKey("jwk", this.keyPair.publicKey);
  }

  async setMediaKeyFromRaw(raw: ArrayBuffer): Promise<void> {
    this.mediaKeyRaw = raw;
    this.mediaKey = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, [
      "encrypt",
      "decrypt",
    ]);
    const digest = await crypto.subtle.digest("SHA-256", raw);
    this.fingerprint = bytesToHex(digest);
  }

  async generateMediaKeyAsHost(): Promise<ArrayBuffer> {
    const raw = crypto.getRandomValues(new Uint8Array(32));
    await this.setMediaKeyFromRaw(raw.buffer);
    return raw.buffer;
  }

  getMediaKeyRaw(): ArrayBuffer | null {
    return this.mediaKeyRaw;
  }

  hasMediaKey() {
    return Boolean(this.mediaKey);
  }

  getFingerprint(): string | null {
    return this.fingerprint;
  }

  async deriveSharedKey(peerPublicJwk: JsonWebKey): Promise<CryptoKey> {
    if (!this.keyPair) throw new Error("missing keypair");
    const peerPublic = await crypto.subtle.importKey(
      "jwk",
      peerPublicJwk,
      { name: "ECDH", namedCurve: "P-256" },
      true,
      [],
    );
    return crypto.subtle.deriveKey(
      { name: "ECDH", public: peerPublic },
      this.keyPair.privateKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  }

  async wrapMediaKeyForPeer(peerPublicJwk: JsonWebKey, mediaKeyRaw: ArrayBuffer) {
    const wrappingKey = await this.deriveSharedKey(peerPublicJwk);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, wrappingKey, mediaKeyRaw);
    return {
      iv: btoa(String.fromCharCode(...iv)),
      ciphertext: btoa(String.fromCharCode(...new Uint8Array(ciphertext))),
    };
  }

  async unwrapMediaKey(fromPeerJwk: JsonWebKey, ivB64: string, cipherB64: string) {
    const wrappingKey = await this.deriveSharedKey(fromPeerJwk);
    const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));
    const cipher = Uint8Array.from(atob(cipherB64), (c) => c.charCodeAt(0));
    const raw = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, wrappingKey, cipher);
    await this.setMediaKeyFromRaw(raw);
  }

  rememberPeerKey(peerId: string, jwk: JsonWebKey) {
    void crypto.subtle
      .importKey("jwk", jwk, { name: "ECDH", namedCurve: "P-256" }, true, [])
      .then((k) => this.peerKeys.set(peerId, k));
  }

  getPeerJwk(peerId: string): CryptoKey | undefined {
    return this.peerKeys.get(peerId);
  }

  async encryptChat(plain: string): Promise<string> {
    if (!this.mediaKey) return plain;
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plain);
    const cipher = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, this.mediaKey, encoded);
    const payload = `${btoa(String.fromCharCode(...iv))}.${btoa(String.fromCharCode(...new Uint8Array(cipher)))}`;
    return `${E2EE_PREFIX}${payload}`;
  }

  async decryptChat(stored: string): Promise<string> {
    if (!stored.startsWith(E2EE_PREFIX)) return stored;
    if (!this.mediaKey) return stored;
    try {
      const body = stored.slice(E2EE_PREFIX.length);
      const dot = body.indexOf(".");
      if (dot < 0) return stored;
      const ivB64 = body.slice(0, dot);
      const cipherB64 = body.slice(dot + 1);
      const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));
      const cipher = Uint8Array.from(atob(cipherB64), (c) => c.charCodeAt(0));
      const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, this.mediaKey, cipher);
      return new TextDecoder().decode(plain);
    } catch {
      return stored;
    }
  }

  async applyToSender(sender: RTCRtpSender) {
    if (!this.insertableSupported || !this.mediaKeyRaw) return;
    try {
      sender.transform = new RTCRtpScriptTransform(
        new URL("../workers/e2ee-worker.ts", import.meta.url),
        { operation: "encrypt", key: this.mediaKeyRaw },
        { name: "e2ee-encrypt" },
      );
    } catch {
      /* browser may block worker transform */
    }
  }

  async applyToReceiver(receiver: RTCRtpReceiver) {
    if (!this.insertableSupported || !this.mediaKeyRaw) return;
    try {
      receiver.transform = new RTCRtpScriptTransform(
        new URL("../workers/e2ee-worker.ts", import.meta.url),
        { operation: "decrypt", key: this.mediaKeyRaw },
        { name: "e2ee-decrypt" },
      );
    } catch {
      /* ignore */
    }
  }

  getStatus(verified: boolean): E2EEStatus {
    return {
      enabled: this.enabled,
      ready: Boolean(this.mediaKey),
      securityCode: this.fingerprint ? formatSecurityCode(this.fingerprint) : null,
      mediaLayer: this.insertableSupported && this.mediaKeyRaw ? "insertable-streams" : "dtls-srtp",
      chatEncrypted: false,
      verified,
    };
  }
}

export function isE2EEChatPayload(content: string) {
  return content.startsWith(E2EE_PREFIX);
}
